import React from 'react';
import { Cpu, Users, Globe, Mail, Github } from 'lucide-react';
import { X } from '../components/Icons';
import PageTitle from '../components/PageTitle';

function About() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <PageTitle 
        title="About"
        description="Learn about QuantumBytz and our mission to bridge quantum computing with modern technology."
        keywords="quantum computing, technology platform, tech community, open source"
      />

      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-[#232323] mb-4">About QuantumBytz</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Your trusted source for quantum computing, Linux, and cutting-edge technology news and insights
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <Cpu className="w-8 h-8 text-[#EC6224] mb-4" />
          <h2 className="text-xl font-semibold mb-3">Our Mission</h2>
          <p className="text-gray-600">
            To democratize quantum computing knowledge and provide cutting-edge insights into Linux and modern technology stacks, making complex technical concepts accessible to everyone.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <Users className="w-8 h-8 text-[#EC6224] mb-4" />
          <h2 className="text-xl font-semibold mb-3">Our Community</h2>
          <p className="text-gray-600">
            QuantumBytz is powered by a passionate community of developers, researchers, and technology enthusiasts who contribute to our growing knowledge base and discussions.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <Globe className="w-8 h-8 text-[#EC6224] mb-4" />
          <h2 className="text-xl font-semibold mb-3">Global Impact</h2>
          <p className="text-gray-600">
            Through our platform, we connect technology enthusiasts worldwide, fostering collaboration and knowledge sharing in quantum computing and open-source technologies.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-8 mb-16">
        <h2 className="text-2xl font-bold mb-6">Our Platform</h2>
        <div className="prose max-w-none">
          <p className="mb-4">
            QuantumBytz aggregates and curates the latest developments in quantum computing, Linux, and emerging technologies from trusted sources worldwide. Our platform features:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>Real-time updates from leading quantum computing research institutions</li>
            <li>Comprehensive Linux tutorials and system administration guides</li>
            <li>In-depth coverage of emerging technology trends</li>
            <li>Community-driven discussions and knowledge sharing</li>
          </ul>
          <p>
            We collaborate with leading technology publications and research institutions to bring you verified and accurate information. Our content is regularly updated to reflect the latest developments in the field.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-8 mb-16">
        <h2 className="text-2xl font-bold mb-6">Content Sources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-4">Research Institutions</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• IBM Quantum Research</li>
              <li>• Google Quantum AI Lab</li>
              <li>• MIT Technology Review</li>
              <li>• QuTech Academy</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-4">Technology Partners</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• Linux Foundation</li>
              <li>• Quantum Economic Development Consortium</li>
              <li>• Open Quantum Initiative</li>
              <li>• IEEE Quantum</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6">Connect With Us</h2>
        <p className="text-gray-600 mb-6">
          Join our community and stay connected with the latest developments in quantum computing and technology.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="https://github.com/quantumbytz"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-6 py-3 bg-[#232323] text-white rounded-lg hover:bg-[#EC6224] transition-colors"
          >
            <Github className="w-5 h-5 mr-2" />
            GitHub
          </a>
          <a
            href="https://x.com/quantumbytz"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-6 py-3 bg-[#232323] text-white rounded-lg hover:bg-[#EC6224] transition-colors"
          >
            <X className="w-5 h-5 mr-2" />
            Twitter
          </a>
          <a
            href="mailto:contact@quantumbytz.com"
            className="flex items-center px-6 py-3 bg-[#232323] text-white rounded-lg hover:bg-[#EC6224] transition-colors"
          >
            <Mail className="w-5 h-5 mr-2" />
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}

export default About;